import * as React from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { AppUser } from 'Src/views/App/App';

export interface AwardEventFormValues {
  awardedAt: string;
  points: number;
  reason?: string;
  awardedByName?: string;
}

interface Props {
  initialValues: AwardEventFormValues;
  onCancel: () => void;
  onSubmit: (values: AwardEventFormValues) => void;
  user: AppUser;
}

const AwardEventSchema = Yup.object().shape({
  awardedAt: Yup.string().required('Required'),
  points: Yup.number().required('Required'),
  reason: Yup.string().nullable(),
  awardedByName: Yup.string().nullable()
});

export const AwardEventForm: React.FC<Props> = ({ initialValues, onCancel, onSubmit, user }) => (
  <Formik
    initialValues={initialValues}
    enableReinitialize
    validationSchema={AwardEventSchema}
    onSubmit={values => {
      const { awardedAt, points, reason, awardedByName } = values;

      const isoDate = new Date(awardedAt).toISOString();

      const defaultName =
        user.profile.firstName && user.profile.lastName
          ? `${user.profile.firstName} ${user.profile.lastName}`
          : 'John Doe';
      const nominaterName = awardedByName?.trim() || defaultName;

      const payload: AwardEventFormValues = {
        awardedAt: isoDate,
        points,
        reason,
        awardedByName: nominaterName
      };

      onSubmit(payload);
    }}
  >
    {({ errors, touched }) => (
      <Form className="schoolAwardsEditForm">
        <div className="schoolAwardsEditForm__grid">
          <label className={errors.awardedAt && touched.awardedAt ? 'schoolAwardsEditField schoolAwardsEditField--error' : 'schoolAwardsEditField'}>
            <span>Award date</span>
            <Field name="awardedAt">
              {({ field }) => (
                <div className="schoolAwardsEditField__date">
                  <input {...field} type="date" />
                </div>
              )}
            </Field>
            {errors.awardedAt && touched.awardedAt && <em>{errors.awardedAt}</em>}
          </label>

          <label className={errors.points && touched.points ? 'schoolAwardsEditField schoolAwardsEditField--error' : 'schoolAwardsEditField'}>
            <span>Points</span>
            <Field type="number" name="points" />
            {errors.points && touched.points && <em>{errors.points}</em>}
          </label>
        </div>

        <label className="schoolAwardsEditField">
          <span>Awarded by</span>
          <Field name="awardedByName">
            {({ field }) => <input {...field} type="text" placeholder="Leave blank to use your name" />}
          </Field>
          <small>Leave blank to default to your name.</small>
        </label>

        <label className="schoolAwardsEditField">
          <span>Reason</span>
          <Field as="textarea" name="reason" rows={4} placeholder="Add the reason or note shown with this award" />
        </label>

        <div className="schoolAwardsEditForm__actions">
          <button type="button" className="schoolAwardsButton schoolAwardsButton--ghost" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="schoolAwardsButton schoolAwardsButton--primary">
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg>
            Save changes
          </button>
        </div>
      </Form>
    )}
  </Formik>
);
